import React, { useCallback, useMemo, useState } from 'react';
import { styled } from '@this/constants/themes';
import _ from 'lodash';

import A from '@this/shared/atoms/a';
import type { FilterableSelectorOption } from '@this/shared/filterable_selector/filterable_selector';

import type ReserveInfo from '@this/domain/reserve_info2';
import OrderItemMapping from '@this/domain/order_item_mapping2';
import type Department from '@this/domain/department/department2';
import { Fetcher } from '@this/src/util';
import type { ExpensesAccountTypeResponseArgs } from '@this/src/domain/expenses/expenses_account_type2';
import ExpensesAccountType, {
  convertExpensesAccountTypeResponseToArgs
} from '@this/src/domain/expenses/expenses_account_type2';
import InputCustomerInformationFilterableSelector from '../input_customer_information_filterable_selector/input_customer_information_filterable_selector';
import SelectDepartments from '../select_departments/select_departments';

interface ExpensesAccountTypeResponse {
  expenses_account_types: ExpensesAccountTypeResponseArgs[];
}

type Props = {
  reserveInfo: ReserveInfo;
  workflowFormWidth: string;
};

const DETAIL_FORM_WIDTH = '240px';
const FORM_WIDTH = '100%';

const ChargingDepartmentSection: React.FC<Props> = ({ reserveInfo, workflowFormWidth }) => {
  const [showDetail, setShowDetail] = useState(false);
  const defaultDepartment = useMemo(
    () =>
      (id: number, item_type: string, index: number): string => {
        const department = reserveInfo.firstExistAccountTraveler?.user?.department;
        const mapping = reserveInfo.orderItemMappingsDepartmentPart.find(
          o => o.travelerId === id && o.orderItemType === item_type && o.orderItemIndex === index
        );

        if (!department) {
          return '';
        }
        const label = `${department.code}:${department.name}`;
        if (department && mapping) {
          mapping.chargingDepartmentId = department.id;
          mapping.chargingDepartmentShareId = undefined;
          mapping.selectedOptionLabel = label;
        }

        return label;
      },
    [reserveInfo.firstExistAccountTraveler, reserveInfo.orderItemMappingsDepartmentPart]
  );

  const resetExpenseAccountType = useCallback(async () => {
    const chargingDepartmentIds = reserveInfo.chargingDepartmentIDs;
    await Fetcher.get<ExpensesAccountTypeResponse>(`/reserve_confirm/expenses_account_types.json`, {
      department_ids: chargingDepartmentIds,
      request_date: Date.now()
    }).then(
      result => {
        reserveInfo.expensesAccountTypes = result.expenses_account_types.map(
          e => new ExpensesAccountType(convertExpensesAccountTypeResponseToArgs(e))
        );
      },
      _error => {
        throw _error;
      }
    );
  }, [reserveInfo]);

  const onSelectDetail = useCallback(
    async (changingMapping: OrderItemMapping, option: FilterableSelectorOption | null) => {
      const values = option?.value.split('-');
      if (changingMapping && values && values?.length === 2) {
        if (values[0] === 'department') {
          changingMapping.chargingDepartmentId = Number(values[1]);
          changingMapping.selectedOptionLabel = option?.label || '';
        } else if (values[0] === 'share') {
          changingMapping.chargingDepartmentShareId = Number(values[1]);
          if (option?.labelLines) {
            changingMapping.selectedOptionLabel = option.labelLines[0] || '';
          }
        }
      } else if (option === null) {
        const department = reserveInfo.firstExistAccountTraveler?.user?.department;
        changingMapping.chargingDepartmentId = department?.id;
        const descriptionWithCode = `${department?.code}:${department?.name}`;
        changingMapping.selectedOptionLabel = descriptionWithCode;
      }

      await resetExpenseAccountType();
    },
    [reserveInfo, resetExpenseAccountType]
  );

  const onSelect = useCallback(
    async (department: Department) => {
      reserveInfo.chargingDepartment = department;
      reserveInfo.orderItemMappingsDepartmentPart = [
        new OrderItemMapping({
          travelerId: -1,
          orderItemType: 'all',
          orderItemIndex: -1,
          chargingDepartmentId: Number(department.id)
        })
      ];
      await resetExpenseAccountType();
      app.render();
    },
    [reserveInfo, resetExpenseAccountType]
  );

  return (
    <>
      <Label>
        費用負担部署
        {reserveInfo.isChargingDepartmentRequired && <Red>*</Red>}
      </Label>
      {showDetail ? (
        <ShareDetailArea workflowFormWidth={workflowFormWidth}>
          {reserveInfo.departmentDetailMode === 'item' ? (
            <>
              <ShareDetailFlexArea>
                <ShareDetailModeSelected>商品ごと</ShareDetailModeSelected>
                <ShareDetailModeLinkArea>
                  <A
                    onClick={() => {
                      reserveInfo.departmentDetailMode = 'traveler';
                    }}
                  >
                    出張者ごと
                  </A>
                </ShareDetailModeLinkArea>
                <ShareDetailModeLinkArea>
                  <A
                    onClick={() => {
                      reserveInfo.departmentDetailMode = 'both';
                    }}
                  >
                    商品×出張者ごと
                  </A>
                </ShareDetailModeLinkArea>
              </ShareDetailFlexArea>
              {reserveInfo.reservingTrip.isPackage ? (
                <ShareDetailFlexArea>
                  <MappingDetailLabel>パッケージ</MappingDetailLabel>
                  <ShareDetailSelectArea>
                    <InputCustomerInformationFilterableSelector
                      placeholder="部署を検索"
                      options={reserveInfo.departmentDetailOptions}
                      selected={{
                        label:
                          reserveInfo.orderItemMappingsDepartmentPart.find(
                            o => o.travelerId === -1 && o.orderItemType === 'package' && o.orderItemIndex === 1
                          )?.selectedOptionLabel || defaultDepartment(-1, 'package', 1),
                        value: '',
                        matcher: ''
                      }}
                      onSelect={option => {
                        const mapping = reserveInfo.orderItemMappingsDepartmentPart.find(
                          o => o.travelerId === -1 && o.orderItemType === 'package' && o.orderItemIndex === 1
                        );
                        if (mapping) {
                          onSelectDetail(mapping, option);
                        }
                      }}
                      isRequired={reserveInfo.isChargingDepartmentRequired}
                      workflowFormWidth={DETAIL_FORM_WIDTH}
                    />
                  </ShareDetailSelectArea>
                </ShareDetailFlexArea>
              ) : (
                <>
                  {reserveInfo.itemList.map((item, i: number) => (
                    <div key={`item${i}`}>
                      <ShareDetailFlexArea>
                        {item?.elementType === 'hotel' ? (
                          <ShareDetailLabel>{i + 1}. ホテル</ShareDetailLabel>
                        ) : (
                          <ShareDetailLabel>{i + 1}. 経路</ShareDetailLabel>
                        )}
                        <ShareDetailSelectArea>
                          <InputCustomerInformationFilterableSelector
                            placeholder="部署を検索"
                            options={reserveInfo.departmentDetailOptions}
                            selected={{
                              label:
                                reserveInfo.orderItemMappingsDepartmentPart.find(
                                  o =>
                                    o.travelerId === -1 &&
                                    o.orderItemType === 'item' &&
                                    o.orderItemIndex === Number(item?.index)
                                )?.selectedOptionLabel || defaultDepartment(-1, 'item', Number(item?.index)),
                              value: '',
                              matcher: ''
                            }}
                            onSelect={option => {
                              const mapping = reserveInfo.orderItemMappingsDepartmentPart.find(
                                o =>
                                  o.travelerId === -1 &&
                                  o.orderItemType === 'item' &&
                                  o.orderItemIndex === Number(item?.index)
                              );
                              if (mapping) {
                                onSelectDetail(mapping, option);
                              }
                            }}
                            isRequired={reserveInfo.isChargingDepartmentRequired}
                            workflowFormWidth={DETAIL_FORM_WIDTH}
                          />
                        </ShareDetailSelectArea>
                      </ShareDetailFlexArea>
                    </div>
                  ))}
                </>
              )}
              {reserveInfo.rentalCarRequired === 'true' && (
                <>
                  {reserveInfo.reservingTrip.rentalCars.map((_rentalCar, car_index) => (
                    <ShareDetailFlexArea key={`car${car_index}`}>
                      <ShareDetailLabel>レンタカー {car_index + 1}</ShareDetailLabel>
                      <ShareDetailSelectArea>
                        <InputCustomerInformationFilterableSelector
                          placeholder="部署を検索"
                          options={reserveInfo.departmentDetailOptions}
                          selected={{
                            label:
                              reserveInfo.orderItemMappingsDepartmentPart.find(
                                o =>
                                  o.travelerId === -1 &&
                                  o.orderItemType === 'rental_car' &&
                                  o.orderItemIndex === car_index
                              )?.selectedOptionLabel || defaultDepartment(-1, 'rental_car', car_index),
                            value: '',
                            matcher: ''
                          }}
                          onSelect={option => {
                            const mapping = reserveInfo.orderItemMappingsDepartmentPart.find(
                              o =>
                                o.travelerId === -1 &&
                                o.orderItemType === 'rental_car' &&
                                o.orderItemIndex === car_index
                            );
                            if (mapping) {
                              onSelectDetail(mapping, option);
                            }
                          }}
                          isRequired={reserveInfo.isChargingDepartmentRequired}
                          workflowFormWidth={DETAIL_FORM_WIDTH}
                        />
                      </ShareDetailSelectArea>
                    </ShareDetailFlexArea>
                  ))}
                </>
              )}
            </>
          ) : reserveInfo.departmentDetailMode === 'traveler' ? (
            <>
              <ShareDetailFlexArea>
                <>
                  <ShareDetailModeLinkArea>
                    <A
                      onClick={() => {
                        reserveInfo.departmentDetailMode = 'item';
                      }}
                    >
                      商品ごと
                    </A>
                  </ShareDetailModeLinkArea>
                  <ShareDetailModeSelected>出張者ごと</ShareDetailModeSelected>
                  <ShareDetailModeLinkArea>
                    <A
                      onClick={() => {
                        reserveInfo.departmentDetailMode = 'both';
                      }}
                    >
                      商品×出張者ごと
                    </A>
                  </ShareDetailModeLinkArea>
                </>
              </ShareDetailFlexArea>
              {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                <ShareDetailFlexArea key={traveler_index}>
                  <ShareDetailLabel>{traveler.travelerName}</ShareDetailLabel>
                  <ShareDetailSelectArea>
                    <InputCustomerInformationFilterableSelector
                      placeholder="部署を検索"
                      options={reserveInfo.departmentDetailOptions}
                      selected={{
                        label:
                          reserveInfo.orderItemMappingsDepartmentPart.find(
                            o =>
                              o.travelerId === traveler_index &&
                              o.orderItemType === 'all' &&
                              o.orderItemIndex === -1
                          )?.selectedOptionLabel || defaultDepartment(traveler_index, 'all', -1),
                        value: '',
                        matcher: ''
                      }}
                      onSelect={option => {
                        const mapping = reserveInfo.orderItemMappingsDepartmentPart.find(
                          o =>
                            o.travelerId === traveler_index && o.orderItemType === 'all' && o.orderItemIndex === -1
                        );
                        if (mapping) {
                          onSelectDetail(mapping, option);
                        }
                      }}
                      isRequired={reserveInfo.isChargingDepartmentRequired}
                      workflowFormWidth={DETAIL_FORM_WIDTH}
                    />
                  </ShareDetailSelectArea>
                </ShareDetailFlexArea>
              ))}
            </>
          ) : reserveInfo.departmentDetailMode === 'both' ? (
            <>
              <ShareDetailFlexArea>
                <ShareDetailModeLinkArea>
                  <A
                    onClick={() => {
                      reserveInfo.departmentDetailMode = 'item';
                    }}
                  >
                    商品ごと
                  </A>
                </ShareDetailModeLinkArea>
                <ShareDetailModeLinkArea>
                  <A
                    onClick={() => {
                      reserveInfo.departmentDetailMode = 'traveler';
                    }}
                  >
                    出張者ごと
                  </A>
                </ShareDetailModeLinkArea>
                <ShareDetailModeSelected>商品×出張者ごと</ShareDetailModeSelected>
              </ShareDetailFlexArea>
              {reserveInfo.reservingTrip.isPackage ? (
                <>
                  <MappingDetailLabel>パッケージ</MappingDetailLabel>
                  <ShareDetailIndentedArea>
                    {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                      <ShareDetailFlexArea key={traveler_index}>
                        <MappingDetailLabel>{traveler.travelerName}</MappingDetailLabel>
                        <ShareDetailSelectArea>
                          <InputCustomerInformationFilterableSelector
                            placeholder="部署を検索"
                            options={reserveInfo.departmentDetailOptions}
                            selected={{
                              label:
                                reserveInfo.orderItemMappingsDepartmentPart.find(
                                  o =>
                                    o.travelerId === traveler_index &&
                                    o.orderItemType === 'package' &&
                                    o.orderItemIndex === 1
                                )?.selectedOptionLabel || defaultDepartment(traveler_index, 'package', 1),
                              value: '',
                              matcher: ''
                            }}
                            onSelect={option => {
                              const mapping = reserveInfo.orderItemMappingsDepartmentPart.find(
                                o =>
                                  o.travelerId === traveler_index &&
                                  o.orderItemType === 'package' &&
                                  o.orderItemIndex === 1
                              );
                              if (mapping) {
                                onSelectDetail(mapping, option);
                              }
                            }}
                            isRequired={reserveInfo.isChargingDepartmentRequired}
                            workflowFormWidth={DETAIL_FORM_WIDTH}
                          />
                        </ShareDetailSelectArea>
                      </ShareDetailFlexArea>
                    ))}
                  </ShareDetailIndentedArea>
                </>
              ) : (
                <>
                  {reserveInfo.itemList.map((item, i: number) => (
                    <div key={`item${i}`}>
                      {item?.elementType === 'hotel' || item?.elementType === 'hotel' ? (
                        <MappingDetailLabel>{i + 1}. ホテル</MappingDetailLabel>
                      ) : (
                        <MappingDetailLabel>{i + 1}. 経路</MappingDetailLabel>
                      )}
                      <ShareDetailIndentedArea>
                        {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                          <ShareDetailFlexArea key={traveler_index}>
                            <MappingDetailLabel>{traveler.travelerName}</MappingDetailLabel>
                            <ShareDetailSelectArea>
                              <InputCustomerInformationFilterableSelector
                                placeholder="部署を検索"
                                options={reserveInfo.departmentDetailOptions}
                                selected={{
                                  label:
                                    reserveInfo.orderItemMappingsDepartmentPart.find(
                                      o =>
                                        o.travelerId === traveler_index &&
                                        o.orderItemType === 'item' &&
                                        o.orderItemIndex === Number(item?.index)
                                    )?.selectedOptionLabel ||
                                    defaultDepartment(traveler_index, 'item', Number(item?.index)),
                                  value: '',
                                  matcher: ''
                                }}
                                onSelect={option => {
                                  const mapping = reserveInfo.orderItemMappingsDepartmentPart.find(
                                    o =>
                                      o.travelerId === traveler_index &&
                                      o.orderItemType === 'item' &&
                                      o.orderItemIndex === Number(item?.index)
                                  );
                                  if (mapping) {
                                    onSelectDetail(mapping, option);
                                  }
                                }}
                                isRequired={reserveInfo.isChargingDepartmentRequired}
                                workflowFormWidth={DETAIL_FORM_WIDTH}
                              />
                            </ShareDetailSelectArea>
                          </ShareDetailFlexArea>
                        ))}
                      </ShareDetailIndentedArea>
                    </div>
                  ))}
                </>
              )}
              {reserveInfo.rentalCarRequired === 'true' && (
                <>
                  {reserveInfo.reservingTrip.rentalCars.map((_rentalCar, car_index) => (
                    <div key={`car${car_index}`}>
                      <MappingDetailLabel>レンタカー {car_index + 1}</MappingDetailLabel>
                      <ShareDetailIndentedArea>
                        {reserveInfo.reservingTrip.travelers.map((traveler, traveler_index) => (
                          <ShareDetailFlexArea key={traveler_index}>
                            <MappingDetailLabel>{traveler.travelerName}</MappingDetailLabel>
                            <ShareDetailSelectArea>
                              <InputCustomerInformationFilterableSelector
                                placeholder="部署を検索"
                                options={reserveInfo.departmentDetailOptions}
                                selected={{
                                  label:
                                    reserveInfo.orderItemMappingsDepartmentPart.find(
                                      o =>
                                        o.travelerId === traveler_index &&
                                        o.orderItemType === 'rental_car' &&
                                        o.orderItemIndex === car_index
                                    )?.selectedOptionLabel ||
                                    defaultDepartment(traveler_index, 'rental_car', car_index),
                                  value: '',
                                  matcher: ''
                                }}
                                onSelect={option => {
                                  const mapping = reserveInfo.orderItemMappingsDepartmentPart.find(
                                    o =>
                                      o.travelerId === traveler_index &&
                                      o.orderItemType === 'rental_car' &&
                                      o.orderItemIndex === car_index
                                  );
                                  if (mapping) {
                                    onSelectDetail(mapping, option);
                                  }
                                }}
                                isRequired={reserveInfo.isChargingDepartmentRequired}
                                workflowFormWidth={DETAIL_FORM_WIDTH}
                              />
                            </ShareDetailSelectArea>
                          </ShareDetailFlexArea>
                        ))}
                      </ShareDetailIndentedArea>
                    </div>
                  ))}
                </>
              )}
            </>
          ) : null}
          <DetailLinkArea>
            <A
              onClick={() => {
                reserveInfo.showDepartmentDetail = false;
                setShowDetail(false);
              }}
            >
              キャンセル
            </A>
          </DetailLinkArea>
        </ShareDetailArea>
      ) : (
        <>
          <InputAreaRight data-testid="charging_department_select">
            <SelectDepartments
              departments={reserveInfo.departments}
              onSelect={onSelect}
              disabledAddress={false}
              user={reserveInfo.firstExistAccountTraveler}
              allowBlank
              isRequired={reserveInfo.isChargingDepartmentRequired}
              workflowFormWidth={FORM_WIDTH}
              defaultDepartment={reserveInfo.chargingDepartment}
            />
          </InputAreaRight>
          {reserveInfo.projectShareAvailability && (
            <DetailLinkArea>
              <A
                onClick={() => {
                  reserveInfo.showDepartmentDetail = true;
                  reserveInfo.initOrderItemMappings('department');
                  setShowDetail(true);
                }}
              >
                詳細設定
              </A>
            </DetailLinkArea>
          )}
        </>
      )}
    </>
  );
};

const Label = styled.div`
  font-size: 13px;
  width: 200px;
  line-height: 34px;
`;

const InputAreaRight = styled.div<{ workflowFormWidth?: string }>`
  line-height: 34px;
  display: flex;
  width: ${props => props.workflowFormWidth || '420px'};
`;
const DetailLinkArea = styled.div`
  font-weight: bold;
  margin-top: 5px;
  margin-left: 8px;
`;

const ShareDetailModeLinkArea = styled.div`
  font-weight: bold;
  margin-top: 8px;
  margin-right: 16px;
`;

const ShareDetailModeSelected = styled.p`
  font-weight: bold;
  margin-top: 8px;
  margin-right: 16px;
`;

const ShareDetailArea = styled.div<{ workflowFormWidth?: string }>`
  display: block;
  margin-bottom: 24px;
  width: ${props => props.workflowFormWidth || '420px'};
`;

const ShareDetailFlexArea = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const MappingDetailLabel = styled.p`
  font-weight: bold;
  margin-top: 4px;
  margin-left: 8px;
`;

const ShareDetailLabel = styled.p`
  font-weight: bold;
  margin-top: 4px;
  margin-left: 8px;
  width: 60px;
`;

const ShareDetailIndentedArea = styled.div`
  margin-top: 8px;
  margin-left: 16px;
  width: ${DETAIL_FORM_WIDTH};
`;

const ShareDetailSelectArea = styled.div`
  width: ${DETAIL_FORM_WIDTH};
`;

const Red = styled.span`
  color: ${props => props.theme.redColor};
`;

export default ChargingDepartmentSection;
